
// dmd.js — the 128x32 dot-matrix display (score / message / attract pages).
// Draws 1px dots into the 128x32 2D canvas owned by the Renderer (renderer.dmdCanvas); main() uploads that canvas
// as a texture every rendered frame (renderer.dmdTex.needsUpdate). Depends on nothing but the DOM canvas API.
// Public names: DMD_FONT, DMD.
//
// Score page layout (rows are 0-based dot rows, 7-row glyphs; scale 2 glyphs are 14 rows):
//   rows  3..16  score, right-aligned at x 126 (scale 2 up to 9 characters; a single-size score sits at rows 3..9
//                beside a mode title and at rows 6..12 on its own)
//   rows  3..9   mode title at x 2: the score drops to scale 1 when the whole title would not fit beside it at
//                scale 2, and the title is then clipped (at a word boundary) to the dots left of the score
//   rows 14..20  bonus multiplier ("2X") at x 2
//   rows 24..30  status row: left slot "BALL n" (or "PULL" + meter while the plunger is pulled), a "PULL PLUNGER"
//                prompt centred in the free span between the slots, right slot "SAVE" at x 104 while ball save runs

// 5x7 bitmap font. Each glyph is 5 columns; each column is a 7-bit mask with bit 0 = top row, bit 6 = bottom row.
// Glyph advance is 6 dots (5 + 1 gap). Unknown characters fall back to '?'.
const DMD_FONT = {
  A: [126, 9, 9, 9, 126],
  B: [127, 73, 73, 73, 54],
  C: [62, 65, 65, 65, 34],
  D: [127, 65, 65, 34, 28],
  E: [127, 73, 73, 73, 65],
  F: [127, 9, 9, 9, 1],
  G: [62, 65, 73, 73, 122],
  H: [127, 8, 8, 8, 127],
  I: [0, 65, 127, 65, 0],
  J: [32, 64, 65, 63, 1],
  K: [127, 8, 20, 34, 65],
  L: [127, 64, 64, 64, 64],
  M: [127, 2, 12, 2, 127],
  N: [127, 4, 8, 16, 127],
  O: [62, 65, 65, 65, 62],
  P: [127, 9, 9, 9, 6],
  Q: [62, 65, 81, 33, 94],
  R: [127, 9, 25, 41, 70],
  S: [70, 73, 73, 73, 49],
  T: [1, 1, 127, 1, 1],
  U: [63, 64, 64, 64, 63],
  V: [31, 32, 64, 32, 31],
  W: [63, 64, 56, 64, 63],
  X: [99, 20, 8, 20, 99],
  Y: [7, 8, 112, 8, 7],
  Z: [97, 81, 73, 69, 67],
  0: [62, 81, 73, 69, 62],
  1: [0, 66, 127, 64, 0],
  2: [66, 97, 81, 73, 70],
  3: [33, 65, 69, 75, 49],
  4: [24, 20, 18, 127, 16],
  5: [39, 69, 69, 69, 57],
  6: [60, 74, 73, 73, 48],
  7: [1, 113, 9, 5, 3],
  8: [54, 73, 73, 73, 54],
  9: [6, 73, 73, 41, 30],
  ' ': [0, 0, 0, 0, 0],
  '-': [8, 8, 8, 8, 8],
  '\u2013': [8, 8, 8, 8, 8], // en dash: same full-width bar as '-'
  '\u2014': [8, 8, 8, 8, 8], // em dash (used by "TOKEN n \u2014 LOCK LIT"): same full-width bar as '-'
  '.': [0, 96, 96, 0, 0],
  ',': [0, 160, 96, 0, 0],
  '!': [0, 0, 95, 0, 0],
  "'": [0, 3, 3, 0, 0],
  ':': [0, 54, 54, 0, 0],
  '/': [96, 16, 8, 4, 3],
  '<': [8, 20, 34, 65, 0],
  '>': [0, 65, 34, 20, 8],
  '=': [20, 20, 20, 20, 20],
  '\u2605': [36, 24, 126, 24, 36], // black star
  '?': [2, 1, 81, 9, 6],
};

/** Score page geometry (dots). */
const DMD_MARGIN_X = 2; // left margin of left-aligned text
const DMD_RIGHT_X = 126; // right edge of right-aligned text
const DMD_GAP = 3; // minimum clear dots between two items on one row
const DMD_STATUS_Y = 24; // top row of the status line
const DMD_SAVE_X = 104; // "SAVE" slot on the status line (ends at x 126)
const DMD_METER_X = 34; // plunger meter start
const DMD_METER_W = 60; // plunger meter width at full pull
const DMD_BONUS_PAGE_S = 2.6; // bonus page lifetime; rules serves the next ball at 2.4 s so this never runs out

class DMD {
  /** @param {HTMLCanvasElement} canvas the 128x32 canvas to draw into (owned by the renderer). */
  constructor(canvas) {
    this.cv = canvas;
    this.ctx = canvas.getContext('2d', { willReadFrequently: false });
    this.t = 0;                 // display clock in seconds; drives blink phases and attract page cycling
    this.msg = null;            // active message: {kind:'flash'|'mode'|'bonus'|'initials', ...} or null
    this.msgT = 0;              // seconds the active message has left
    this.score = 0;
    this.ball = 1;
    this.state = 'ATTRACT';
    this.ballSave = false;
    this.power = 0;             // plunger pull 0..1 (LAUNCH state only)
    this.bonusX = 1;
    this.attractData = { hiscores: [], last: 0 };
    this.attractT = 0;
    this.mode = { title: '', sub: '' };
    this.dirty = true;
  }

  /** Light one dot at grey level `brightness` (0..1, clamped at 1). Off-screen dots are ignored. */
  px(x, y, brightness = 1) {
    if (x < 0 || x >= 128 || y < 0 || y >= 32) return;
    const level = Math.round(Math.min(1, brightness) * 255);
    this.ctx.fillStyle = `rgb(${level},${level},${level})`;
    this.ctx.fillRect(x, y, 1, 1);
  }

  /** Black out the whole display. */
  clear() {
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, 128, 32);
  }

  /** Draw one glyph at (x, y) with an integer `scale`; returns the advance width (6 * scale). */
  glyph(ch, x, y, brightness = 1, scale = 1) {
    const columns = DMD_FONT[ch.toUpperCase()] || DMD_FONT['?'];
    for (let col = 0; col < 5; col++) {
      for (let row = 0; row < 7; row++) {
        if ((columns[col] >> row) & 1) {
          // Each font dot becomes a scale x scale block.
          for (let dx = 0; dx < scale; dx++) {
            for (let dy = 0; dy < scale; dy++) this.px(x + col * scale + dx, y + row * scale + dy, brightness);
          }
        }
      }
    }
    return 6 * scale;
  }

  /** Draw a string left-aligned at (x, y); returns the total advance. */
  text(str, x, y, brightness = 1, scale = 1) {
    let cursor = x;
    for (const ch of str) cursor += this.glyph(ch, cursor, y, brightness, scale);
    return cursor - x;
  }

  /** Width in dots of `str` (advances minus the trailing gap); 0 for an empty string. */
  textW(str, scale = 1) {
    const n = Array.from(str).length;
    return n ? n * 6 * scale - scale : 0;
  }

  /** Draw a string horizontally centered on the 128-dot display. */
  center(str, y, brightness = 1, scale = 1) {
    return this.text(str, Math.floor((128 - this.textW(str, scale)) / 2), y, brightness, scale);
  }

  /**
   * Longest prefix of `str` that fits in `maxW` dots at `scale`, cut at whole glyphs and, when the cut lands
   * inside a word, at the last word boundary before it (trailing spaces trimmed). '' when nothing fits.
   */
  fitText(str, maxW, scale = 1) {
    const chars = Array.from(str);
    let count = Math.floor((maxW + scale) / (6 * scale)); // n glyphs need n*6*scale - scale dots
    count = Math.max(0, Math.min(chars.length, count));
    if (count < chars.length) {
      const cut = chars.lastIndexOf(' ', count); // count itself is fine: a space there means a clean word end
      if (cut > 0) count = cut;
    }
    return chars.slice(0, count).join('').replace(/\s+$/, '');
  }

  /** Full-width horizontal line at row y. */
  hline(y, brightness) {
    for (let x = 0; x < 128; x++) this.px(x, y, brightness);
  }

  /** Horizontal line from x0 (inclusive) to x1 (exclusive) at row y. */
  hlineSeg(x0, x1, y, brightness) {
    for (let x = x0; x < x1; x++) this.px(x, y, brightness);
  }

  /** Consume a Game 'dmd' fx event ({kind, ...}); unknown kinds are ignored. */
  handle(ev) {
    if (ev.kind === 'flash') {
      this.msg = { kind: 'flash', text: ev.text };
      this.msgT = (ev.ms || 1200) / 1e3;
    } else if (ev.kind === 'mode') {
      this.mode = { title: ev.title, sub: ev.sub };
      // A titled mode change is announced for 2 s; an empty title just clears the corner label.
      if (ev.title) {
        this.msg = { kind: 'mode' };
        this.msgT = 2;
      }
    } else if (ev.kind === 'bonus') {
      // {base, x, shown} (+ optional `total`, default base * x): `shown` is the running total of the count-up;
      // rules re-emits the event on every tick as it grows (absent = show the full total). A repeat for the same
      // bonus only updates the number on the open page instead of restarting it.
      const cur = this.msg;
      const total = ev.total ?? ev.base * ev.x;
      if (cur && cur.kind === 'bonus' && cur.base === ev.base && cur.x === ev.x) {
        cur.shown = ev.shown;
        cur.total = total;
        this.msgT = Math.max(this.msgT, 0.8); // a late tick keeps the page up long enough to be read
      } else {
        this.msg = { kind: 'bonus', base: ev.base, x: ev.x, total, shown: ev.shown };
        this.msgT = DMD_BONUS_PAGE_S;
      }
    } else if (ev.kind === 'initials') {
      // Initials entry stays up "forever"; draw() also keeps it alive once msgT runs out.
      this.msg = { kind: 'initials', chars: ev.chars, idx: ev.idx, score: ev.score };
      this.msgT = 999;
    } else if (ev.kind === 'attract') {
      this.attractData = { hiscores: ev.hiscores, last: ev.last };
      this.msg = null;
    }
  }

  /** Consume a Game 'score' status event (score, ball, state, ballSave, power, bonusX). */
  setStatus(status) {
    this.score = status.score;
    this.ball = status.ball;
    this.state = status.state;
    this.ballSave = status.ballSave;
    this.power = status.power;
    this.bonusX = status.bonusX;
  }

  /** Thousands-separated score text. */
  fmtScore(n) {
    return n.toLocaleString('en-US');
  }

  /** Advance the display clock by dt seconds and redraw the whole canvas; returns the canvas. */
  draw(dt) {
    this.t += dt;
    if (this.msgT > 0) this.msgT -= dt;
    else this.msg = this.msg?.kind === 'initials' ? this.msg : null;
    this.clear();
    if (this.state === 'ATTRACT' || this.state === 'OVER') this.drawAttract();
    else if (this.msg) this.drawMsg();
    else this.drawScore();
    return this.cv;
  }

  /** In-game page: big score right-aligned, mode title, bonus multiplier, status row (see the layout above). */
  drawScore() {
    const scoreText = this.fmtScore(this.score);
    // Mode label in the top-left corner unless it just repeats the ball number.
    const title = this.mode.title && this.mode.title !== 'BALL ' + this.ball ? this.mode.title : '';
    // Scores of 10+ characters drop to single size to fit; so does a shorter score when the whole title would
    // not fit beside it at scale 2 (a 9-character score leaves 15 dots for the title: two glyphs, which reads as
    // garbage). The title is still clipped afterwards when even the single-size score leaves too little room.
    let scale = scoreText.length <= 9 ? 2 : 1;
    const titleW = this.textW(title);
    if (scale === 2 && title && DMD_MARGIN_X + titleW + DMD_GAP > DMD_RIGHT_X - this.textW(scoreText, 2)) scale = 1;
    const scoreX = DMD_RIGHT_X - this.textW(scoreText, scale);
    const label = title ? this.fitText(title, scoreX - DMD_GAP - DMD_MARGIN_X) : '';
    // A single-size score shares the title's top row so the two read as one line; on its own it sits optically
    // centred in the rows a double-size score would have used.
    this.text(scoreText, scoreX, scale === 2 || label ? 3 : 6, 1, scale);
    if (label) this.text(label, DMD_MARGIN_X, 3, 0.75);
    if (this.bonusX > 1) this.text(this.bonusX + 'X', DMD_MARGIN_X, 14, 0.6);

    // Status row. Left slot: BALL n, replaced by the plunger meter while the plunger is pulled (no overprint).
    const y = DMD_STATUS_Y;
    const pulling = this.state === 'LAUNCH' && this.power > 0.02;
    let freeX0;
    if (pulling) {
      this.text('PULL', DMD_MARGIN_X, y, 0.5);
      // Plunger pull meter: up to 60 dots wide, brightening toward full pull.
      const bars = Math.round(this.power * DMD_METER_W);
      for (let i = 0; i < bars; i++) {
        for (let j = 0; j < 4; j++) this.px(DMD_METER_X + i, y + 1 + j, 0.4 + 0.6 * (i / DMD_METER_W));
      }
      freeX0 = DMD_METER_X + DMD_METER_W + DMD_GAP;
    } else {
      const ballText = 'BALL ' + this.ball;
      this.text(ballText, DMD_MARGIN_X, y, 0.5);
      freeX0 = DMD_MARGIN_X + this.textW(ballText) + DMD_GAP;
    }
    // Right slot: SAVE blinks at 2 Hz; the slot stays reserved through the off phase so the prompt does not jump.
    const freeX1 = this.ballSave ? DMD_SAVE_X - DMD_GAP : 128;
    if (this.ballSave && ((this.t * 4) | 0) % 2) this.text('SAVE', DMD_SAVE_X, y, 0.8);
    // Prompt: PULL PLUNGER blinks at 0.75 Hz centred in the free span; a shorter word when the span is narrow.
    if (this.state === 'LAUNCH' && !pulling && ((this.t * 1.5) | 0) % 2) {
      const free = freeX1 - freeX0;
      const prompt = ['PULL PLUNGER', 'PLUNGER'].find((s) => this.textW(s) <= free);
      if (prompt) this.text(prompt, freeX0 + Math.floor((free - this.textW(prompt)) / 2), y, 0.65);
    }
  }

  /** Message pages: flash text, mode announcement, end-of-ball bonus, initials entry. */
  drawMsg() {
    const msg = this.msg;
    if (msg.kind === 'flash') {
      const on = ((this.t * 6) | 0) % 2; // 3 Hz blink between full and dim
      const words = msg.text.split(' ');
      if (this.textW(msg.text, 2) <= 126) {
        this.center(msg.text, 9, on ? 1 : 0.55, 2);
      } else if (this.textW(msg.text, 1) <= 126) {
        this.center(msg.text, 13, on ? 1 : 0.55, 1);
      } else {
        // Too wide even at 1x: split the words over two lines.
        const half = Math.ceil(words.length / 2);
        this.center(words.slice(0, half).join(' '), 6, on ? 1 : 0.55);
        this.center(words.slice(half).join(' '), 18, on ? 1 : 0.55);
      }
    } else if (msg.kind === 'mode') {
      this.center(this.mode.title, 5, 1, this.textW(this.mode.title, 2) <= 126 ? 2 : 1);
      if (this.mode.sub) this.center(this.mode.sub, 23, 0.6);
    } else if (msg.kind === 'bonus') {
      // Header, then the running total (the count-up value when rules animates it, else base * x) with a
      // progress line underneath that fills as the count-up completes.
      this.center('BONUS ' + this.fmtScore(msg.base) + ' X' + msg.x, 3, 0.9);
      const total = msg.total;
      const shown = Math.max(0, Math.round(msg.shown ?? total));
      const totalText = this.fmtScore(shown);
      this.center(totalText, 14, 1, this.textW(totalText, 2) <= 126 ? 2 : 1);
      const frac = total > 0 ? Math.min(1, shown / total) : 1;
      this.hlineSeg(0, Math.round(frac * 128), 30, frac < 1 ? 0.35 : 0.7);
    } else if (msg.kind === 'initials') {
      // Three evenly spaced rows: caption, score, letters (10-dot pitch) with the cursor underline right below.
      this.center('GREAT SCORE', 2, 0.7);
      this.center(this.fmtScore(msg.score), 11, 0.9);
      const letters = msg.chars.map((code) => String.fromCharCode(code));
      const pitch = 10;
      let x = Math.floor((128 - ((letters.length - 1) * pitch + 5)) / 2);
      letters.forEach((letter, i) => {
        const blinkOn = i === msg.idx && ((this.t * 3) | 0) % 2; // the selected letter blinks at 1.5 Hz
        this.text(letter, x, 21, blinkOn ? 1 : 0.55, 1);
        if (i === msg.idx) this.hlineSeg(x, x + 5, 29, 0.8); // cursor underline
        x += pitch;
      });
    }
  }

  /** Attract loop: four 4.2 s pages (logo sweep, press start, high scores, last score / rules). */
  drawAttract() {
    const page = ((this.t / 4.2) % 4) | 0;
    const phase = (this.t % 4.2) / 4.2; // 0..1 within the current page
    if (page === 0) {
      const sweep = Math.min(1, phase * 1.8);
      const title = 'X-ARCADE';
      const width = this.textW(title, 2);
      const x = Math.floor((128 - width) / 2);
      this.text(title, x, 4, 1, 2);
      // A vertical scan line sweeps left to right across the logo during the first 55% of the page.
      const col = Math.floor(sweep * 128);
      for (let y = 0; y < 32; y++) this.px(col, y, 0.5);
      this.center('TOKEN STORM', 22, 0.7 + 0.3 * Math.sin(this.t * 5));
    } else if (page === 1) {
      this.center('PRESS START', 8, ((this.t * 2) | 0) % 2 ? 1 : 0.4, 1);
      this.center('FLIPPERS: SHIFT KEYS', 20, 0.5); // 20 glyphs = 119 dots: the widest line that fits whole
    } else if (page === 2) {
      this.center('HIGH SCORES', 1, 0.8);
      this.attractData.hiscores.slice(0, 3).forEach((entry, i) => {
        const y = 9 + i * 8; // 8-dot pitch: one clear row between the 7-row glyphs (rows 9..15, 17..23, 25..31)
        const scoreText = this.fmtScore(entry.score);
        const scoreX = 116 - this.textW(scoreText);
        // Rank + name, or just the name when a very wide score (>= 1e9) would run into it.
        const withRank = i + 1 + ' ' + entry.name;
        const label = 12 + this.textW(withRank) + DMD_GAP <= scoreX ? withRank : this.fitText(entry.name, scoreX - 15);
        this.text(label, 12, y, 0.6);
        this.text(scoreText, scoreX, y, 0.6);
      });
    } else if (this.attractData.last > 0) {
      this.center('LAST SCORE', 4, 0.7);
      this.center(this.fmtScore(this.attractData.last), 15, 0.95, 1);
    } else {
      this.center('3 BALLS', 6, 0.8);
      this.center('LOCK 2 FOR MULTIBALL', 20, 0.6);
    }
  }
}
